/**
 * Copy + numbers for the /services/social-ai page. Single source of truth —
 * every figure here is pulled from Meta Graph API + TikTok Marketing API on
 * 14 Sep 2026 (see BRIEF-datacode-social-ai.md). No invented stats, no
 * testimonials, no client quotes. Before = Apr+May 2026 avg/month; after =
 * Jun+Jul+Aug 2026 avg/month; Datacode started managing in June 2026.
 */

export const heroCaption = {
  before: '734',
  after: '42,000',
  detail: 'Instagram accounts reached per month · Swan Village · 57×',
};

/** Four headline multipliers, StatGrid-shaped. */
export const proofStats = [
  { value: '57×', label: 'Instagram accounts reached' },
  { value: '6.6×', label: 'Instagram views' },
  { value: '33×', label: 'Facebook video views' },
  { value: '50×', label: 'Facebook post engagements' },
];

/** One before/after bar block — four pairs, Apr–May vs Jun–Aug 2026. */
export const beforeAfterPairs = [
  { label: 'Instagram accounts reached / mo', before: 734, after: 42000 },
  { label: 'Instagram views / mo', before: 14600, after: 96700 },
  { label: 'Facebook video views / mo', before: 934, after: 30956 },
  { label: 'Facebook post engagements / mo', before: 52, after: 2619 },
];

export const beforeAfterCaption = 'Apr–May avg vs Jun–Aug avg, 2026';

/** Mono detail list under the bar block. */
export const proofDetails = [
  'Peaks in August: 63,425 accounts reached, 117,403 views',
  'Profile visits 3×, website clicks 1.8×, interactions 6.9×',
  'New followers 48/mo → 129/mo',
  'Instagram followers 32,385 (11 Jul) → 33,840 (14 Sep) · Facebook 32,117',
  'TikTok Spark Ads, Jun–Aug: 303,000 IQD ($231) → 1.27M plays, 7,513 likes, 2,311 profile visits, 461 follows',
  'Meta ads, Aug: $13.58 → 44,900 people reached, 21,000 video views',
];

/** Gaya Artis — Meta conversation ads, Kurdish copy. */
export const gayaArtis = {
  headline: '$0.41 per conversation',
  window: 'Apr–Sep 2026',
  details: [
    '$413.79 → 1,002 customer conversations started (IG DM, Messenger, WhatsApp) = $0.41 each',
    '488k people reached · CTR 1.2% (Apr) → 5.2% (Sep) · 93% of conversations got a first reply',
  ],
  sinceStart: 'Since Dec 2025: 2,434 conversations for $976 ($0.40 each), 1.5M reached',
};

export const reportLinkLabel = 'See a full monthly report';

/**
 * Month-by-month series for the MonthlyChart grids. Five full months,
 * Apr–Aug 2026, pulled from Meta Graph API + TikTok Marketing API on
 * 14 Sep 2026 (see BRIEF-monthly-charts.md). Datacode started managing
 * Swan Village in June 2026 (index 2) — Gaya Artis has been managed
 * since Dec 2025, so its charts carry no start marker.
 */
export const chartMonths = ['Apr', 'May', 'Jun', 'Jul', 'Aug'];

export const swanMonthly = {
  igAccountsReached: [580, 887, 28211, 34365, 63425],
  igViews: [9933, 19259, 68182, 104573, 117403],
  igProfileVisits: [916, 1606, 2758, 4061, 4627],
  fbVideoViews: [597, 1270, 8517, 25778, 58574],
  fbPostEngagements: [39, 65, 804, 5345, 1708],
  fbNewFollowers: [29, 67, 76, 212, 98],
  tiktokVideoPlays: [0, 0, 454568, 422986, 394082],
};

export const gayaMonthly = {
  conversationsStarted: [11, 142, 105, 347, 286],
  peopleReached: [121269, 86397, 46461, 141009, 66600],
  clickThroughRate: [1.2, 3.0, 2.9, 3.1, 4.2],
};

export const swanChartCaption = 'Apr–Aug 2026 · full months · Meta Graph API + TikTok Marketing API';

/** The 8 numbered service rows, in brief order. */
export const serviceRows = [
  {
    title: 'Verification & legitimacy',
    body: 'Meta business verification, verified pages, and a business portfolio — category, bio, contact buttons and WhatsApp click-to-chat rebuilt so the business looks legitimate the moment someone lands on it.',
  },
  {
    title: 'Lead capture, actually captured',
    body: 'Every comment, DM and WhatsApp message across every channel lands in one pipeline — Sales24 (Salesmate) — with an AI host that answers in any dialect and qualifies the lead. A human on the team confirms before anything is promised.',
  },
  {
    title: 'Targeted posts, not noise',
    body: 'Nothing goes out without a target: audience, city, time and objective are set before a post is ever scheduled.',
  },
  {
    title: 'Market analysis before post one',
    body: 'Who buys, where, when, and what competitors are already posting — mapped with AI before we publish anything.',
  },
  {
    title: 'Audit, then a 3-month plan',
    body: "We audit the existing accounts and their performance, then start a 3-month plan with monthly checkpoints — what one month of that plan looks like is the proof above.",
  },
  {
    title: 'Your team, not just ours',
    body: 'The client\'s team learns the CRM, the playbooks and the approval gate, so lead capture keeps improving in-house too.',
  },
  {
    title: 'AI on shift every day',
    body: 'For every platform we target, AI checks performance daily and suggests when to post, what to promote and where. A human reviews everything before it ships.',
  },
  {
    title: 'A monthly report that says what moved',
    body: 'What was done, and exactly how each KPI moved — no vanity metrics, the same numbers you see on this page.',
  },
];

/** Three monthly checkpoints + the daily AI loop. */
export const monthlyCheckpoints = [
  { month: 'Month 1', body: 'Audit, verification, CRM live' },
  { month: 'Month 2', body: 'Targeted posting + ads' },
  { month: 'Month 3', body: 'Report + re-plan' },
];

export const dailyLoop =
  'AI checks performance → suggests when / what / where → a human reviews → it ships → every reply lands in the CRM';

/** The one marquee — managed brands, names only. */
export const managedBrands = [
  'Swan Village',
  'Gaya Artis',
  'Catz Restaurant',
  'Exchange Programmes',
  'Asco Village',
];

/** Pricing band — one retainer + client-funded ad spend + usage at cost. */
export const pricing = {
  headline: 'from $500 / mo',
  items: [
    {
      title: 'AI + management retainer',
      body: 'CRM setup, WhatsApp campaigns + automation, video & image generation, human review of everything, website SEO + AI-agent optimisation, a self-improving brand AI, and the monthly report.',
    },
    {
      title: 'Ad spend, your card',
      body: '$150/mo, ramping +$50/mo. The account, pixel and audiences stay yours — we never float media.',
    },
    {
      title: 'Usage at cost',
      body: '$0.09 per WhatsApp marketing message, inbound replies free. AI conversations run about $8 per 1,000.',
    },
  ],
  closingLine: 'the plan is eight systems, not a calendar.',
};
